import { useEffect, useState } from "react";
import { ApiError, type ApiClient } from "../../api/client";
import type { LineRequest, OrderContent, PricedLine, Quote } from "../../api/types";
import { fingerprint } from "./cart";

const DEBOUNCE_MS = 120;

export interface QuoteState {
  /** The latest answer; it may belong to an earlier version of the order (see `fresh`). */
  quote: Quote | null;
  /** The quote matches exactly what is on the screen now. */
  fresh: boolean;
  /** The server refused the current content (e.g. an option that no longer exists). */
  error: ApiError | null;
  /** The priced version of a line, if the latest quote contains exactly this line. */
  priced: (line: LineRequest) => PricedLine | undefined;
}

interface Answer {
  /** The revision and the content it answers. */
  for: string;
  content: string;
  quote: Quote | null;
  error: ApiError | null;
}

/**
 * Asks the server what `content` costs (ADR 0007), shortly after the last change. Older answers
 * never overwrite newer ones: each change cancels the request before it. `revision` changes when
 * the prices may have changed without the ticket changing (a new menu, other actions): the same
 * ticket is then priced again.
 */
export function useQuote(api: ApiClient, content: OrderContent | null, revision = ""): QuoteState {
  const body = content === null ? null : JSON.stringify(content);
  const key = body === null ? null : `${revision}\n${body}`;
  const [answer, setAnswer] = useState<Answer | null>(null);

  useEffect(() => {
    if (key === null || body === null) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api
        .request<Quote>("POST", "/orders/quote", {
          body: JSON.parse(body) as unknown,
          retry: true,
          signal: controller.signal,
        })
        .then((quote) => {
          setAnswer({ for: key, content: body, quote, error: null });
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          const failure = error instanceof ApiError ? error : new ApiError(0, "unexpected", String(error));
          setAnswer((previous) => ({ for: key, content: body, quote: previous?.quote ?? null, error: failure }));
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, key, body]);

  if (key === null) return { quote: null, fresh: false, error: null, priced: () => undefined };
  const current = answer?.for === key;
  return {
    quote: answer?.quote ?? null,
    fresh: current && answer.error === null,
    error: current ? answer.error : null,
    priced: pricedLines(answer),
  };
}

/** Matches the lines of the quoted request with the priced lines of the answer. */
function pricedLines(answer: Answer | null): (line: LineRequest) => PricedLine | undefined {
  if (answer?.quote == null) return () => undefined;
  const requested = (JSON.parse(answer.content) as OrderContent).lines;
  const byContent = new Map<string, PricedLine>();
  requested.forEach((line, index) => {
    const priced = answer.quote?.lines[index];
    if (priced) byContent.set(fingerprint(line), priced);
  });
  return (line) => byContent.get(fingerprint(line));
}
